import { useState } from 'react';
import type { SimulationResult } from '../hooks/useSolarSimulation';
import type { Project } from '../utils/projects';
import './ShareButton.css';

interface ShareButtonProps {
  simulation: SimulationResult | null;
  currentProject: Project | null | undefined;
  siteUrl: string;
}

export function ShareButton({ simulation, currentProject, siteUrl }: ShareButtonProps) {
  const [showShareOptions, setShowShareOptions] = useState(false);

  const generateShareText = () => {
    if (!simulation || !currentProject) return '';

    const power = simulation.current_power_mw.toFixed(1);
    const energy = simulation.daily_energy_mwh.toFixed(1);
    const efficiency = (simulation.current_efficiency * 100).toFixed(0);
    const homes = simulation.homes_powered.toLocaleString();

    return `🌞 Solar Farm Reality Check!

${currentProject.name} is currently generating ${power} MW of power, producing ${energy} MWh of clean energy today and powering ${homes} homes.

Efficiency: ${efficiency}% | Capacity: ${simulation.nameplate_capacity_mw} MW

See the real impact: ${siteUrl}

#SolarReality #RenewableEnergy #ClimateAction`;
  };

  const generateShareUrl = () => {
    return `${siteUrl}?project=${currentProject?.id || 'default'}`;
  };

  const shareToPlatform = (platform: string) => {
    const text = encodeURIComponent(generateShareText());
    const url = encodeURIComponent(generateShareUrl());

    let shareUrl = '';

    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${text}%20${url}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
        break;
      case 'telegram':
        shareUrl = `https://t.me/share/url?url=${url}&text=${text}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(`${generateShareText()}\n\n${generateShareUrl()}`);
        setShowShareOptions(false);
        return;
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
    setShowShareOptions(false);
  };

  const handleShareClick = () => {
    if (navigator.share && window.innerWidth <= 768) {
      // Use native share on mobile
      navigator.share({
        title: 'Solar Farm Reality Check',
        text: generateShareText(),
        url: generateShareUrl(),
      });
    } else {
      setShowShareOptions(!showShareOptions);
    }
  };

  if (!simulation || !currentProject) return null;

  return (
    <div className="share-button-container">
      <button
        className="share-button"
        onClick={handleShareClick}
        title="Share this solar farm simulation"
      >
        📤 Share
      </button>

      {showShareOptions && (
        <div className="share-options">
          <div className="share-options-header">
            <h4>Share Solar Farm Data</h4>
            <button
              className="share-close"
              onClick={() => setShowShareOptions(false)}
            >
              ×
            </button>
          </div>

          <div className="share-platforms">
            <button
              className="share-platform facebook"
              onClick={() => shareToPlatform('facebook')}
            >
              📘 Facebook
            </button>

            <button
              className="share-platform twitter"
              onClick={() => shareToPlatform('twitter')}
            >
              🐦 Twitter
            </button>

            <button
              className="share-platform whatsapp"
              onClick={() => shareToPlatform('whatsapp')}
            >
              💬 WhatsApp
            </button>

            <button
              className="share-platform linkedin"
              onClick={() => shareToPlatform('linkedin')}
            >
              💼 LinkedIn
            </button>

            <button
              className="share-platform telegram"
              onClick={() => shareToPlatform('telegram')}
            >
              ✈️ Telegram
            </button>

            <button
              className="share-platform copy"
              onClick={() => shareToPlatform('copy')}
            >
              📋 Copy Link
            </button>
          </div>

          <div className="share-preview">
            <small>Preview:</small>
            <div className="share-text-preview">
              {generateShareText().split('\n').slice(0, 3).join('\n')}...
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
